import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useFormik } from "formik"
import * as Yup from "yup"
import moment from 'moment'
import { useNavigate } from 'react-router-dom'
import { useParams } from 'react-router-dom'

// components
import AppCard from '../../components/AppCard'
import AppForm from '../../components/form/form-company-time-off/AppForm'
import ActionButtonForm from 'src/components/form/general-input-form/ActionButtonForm'
import AppModalConfirmation from '../../components/modal/AppModalConfirmation'

// helpers
import Axios from '../../helpers/axios'
import useToastMessage from 'src/helpers/toast-message'
import { singleCompanyTimeOffTypeOptionSelect } from 'src/helpers/format-options'

const EditCompanyTimeOff = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { showSuccess, showError } = useToastMessage();
  const params = useParams()
  const { id } = params

  const isLoading = useSelector((state) => state.isLoading)

  const [companyTimeOffDetail, setCompanyTimeOffDetail] = useState('')

  const [showModalConfirm, setShowModalConfirm] = useState({ show: false })

  const formik = useFormik({
    initialValues: {
      company_time_off_name: companyTimeOffDetail?.name,
      company_time_off_start_date: companyTimeOffDetail?.start_date,
      company_time_off_end_date: companyTimeOffDetail?.end_date,
      company_time_off_type: companyTimeOffDetail?.type,
      company_time_off_notes: companyTimeOffDetail?.additional_notes,
    },
    validationSchema: Yup.object().shape({
      company_time_off_name: Yup.string()
        .required("Please enter company time off name."),
      company_time_off_start_date: Yup.string()
        .required("Please enter company time off start date."),
      company_time_off_type: Yup.string()
        .required("Please enter company time off type."),
    }),
    enableReinitialize: true,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async (values, { setSubmitting }) => {
      handleSave(values)
      setSubmitting(false)
    },
  });

  useEffect(() => {
    fetchDetailCompanyTimeOff()
  }, [id])

  const fetchDetailCompanyTimeOff = () => {
    Axios({
      method: "get",
      url: `/api/tracker/v1/company-time-off/${id}`,
    })
      .then(({ data }) => {
        if (data?.success) {
          setCompanyTimeOffDetail(data?.value)
        }
      })
      .catch((err) => {
        console.error(err);
        const errMsg = err?.response?.data?.message
        showError(errMsg)
      });
  }

  const handleOnDateChange = (date, name) => {
    formik.setValues((values) => ({
      ...values,
      [name]: date ? moment(date).format('YYYY-MM-DD') : ""
    }), false)
  }

  const handleInputValue = (values) => {
    const payload = {
      name: values?.company_time_off_name,
      additional_notes: values?.company_time_off_notes,
      start_date: values?.company_time_off_start_date,
      end_date: values?.company_time_off_end_date,
      type: values?.company_time_off_type
    }
    return payload
  }

  const handlePost = (payload) => {
    dispatch({
      type: 'SET_IS_LOADING',
      isLoading: true
    })
    Axios({
      method: "post",
      url: `/api/tracker/v1/company-time-off/update/${id}`,
      data: payload
    })
      .then(({ data }) => {
        if (data?.success) {
          showSuccess('Company time off has been successfully updated')
          navigate(`/company-time-off/details/${id}`)
        }
      })
      .catch((err) => {
        console.error(err);
        const errMsg = err?.response?.data?.message
        showError(errMsg)
      })
      .finally(() => {
        dispatch({
          type: 'SET_IS_LOADING',
          isLoading: false
        })
      })
  }

  const handleSelectType = (e) => {
    const isChecked = e.target.checked
    const name = e.target.name
    
    if (name === 'company_time_off_type_public' && isChecked) {
      formik.setValues((values) => ({
        ...values,
        company_time_off_type: 'public'
      }), false)
    } else if (name === 'company_time_off_type_internal' && isChecked) {
      formik.setValues((values) => ({
        ...values,
        company_time_off_type: 'internal'
      }), false)
    }
  }

  const handleSave = (values) => {
    const inputPayload = handleInputValue(values)
    handlePost(inputPayload)
  }

  const handleSubmit = () => {
    formik.handleSubmit()

    setShowModalConfirm({ show: false })
  }

  return (
    <>
      {
        showModalConfirm.show && (
          <AppModalConfirmation
            confirmationType="confirmation"
            showModal={showModalConfirm}
            setShowModal={setShowModalConfirm}
            headerTitle="Confirmation"
            descriptionText="Are you sure want to edit this company time off?"
            handleSubmit={handleSubmit}
            disabled={formik.isSubmitting}
            isLoading={isLoading}
          />
        )
      }
      <AppCard
        className='mb-4'
        headerTitle={<h4>Edit Company Time Off</h4>}
        bodyContent={
          <AppForm
            values={formik.values}
            handleChange={formik.handleChange}
            typeOptions={singleCompanyTimeOffTypeOptionSelect()}
            handleSelectType={handleSelectType}
            errors={formik.errors}
            onDateChange={handleOnDateChange}
          />
        }
        footerContent={
          <ActionButtonForm
            isLoading={isLoading}
            disabled={formik.isSubmitting}
            onCancel={() => navigate(`/company-time-off/details/${id}`)}
            onSave={() => setShowModalConfirm({ show: true })}
          />
        }
      />
    </>
  )
}

export default EditCompanyTimeOff
